import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Key, Boxes, FileText, ListChecks, Sparkles, ChevronLeft, ChevronRight, Check, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { id: "identity",  label: "Identity",  icon: UserPlus,   hint: "Who they are" },
  { id: "access",    label: "Access",    icon: Key,        hint: "Projects + channels" },
  { id: "equipment", label: "Equipment", icon: Boxes,      hint: "Assets / procurement" },
  { id: "docs",      label: "Documents", icon: FileText,   hint: "NDA, ID, bank" },
  { id: "plan",      label: "First-week",icon: ListChecks, hint: "Onboarding checklist" },
  { id: "review",    label: "Review",    icon: Sparkles,   hint: "Send the invite" },
];

const DEFAULT_DOCS = ["NDA", "Employment contract", "ID proof", "Bank details", "PAN", "Address proof"];
const blank = () => ({
  email: "", name: "", role: "designer", business_unit: "BU1", title: "", manager_id: "",
  start_date: new Date().toISOString().slice(0, 10), employee_type: "full_time",
  project_ids: [], asset_ids: [], procure_assets: [], request_documents: ["NDA", "ID proof", "Bank details"],
  plan_template: "default", custom_checklist: [],
});

export default function EmployeeOnboardingWizard({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(blank);
  const [projects, setProjects] = useState([]);
  const [managers, setManagers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [docDraft, setDocDraft] = useState("");
  const [planDraft, setPlanDraft] = useState("");
  const [procureDraft, setProcureDraft] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep(0); setForm(blank());
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    api.get("/users", { params: { kind: "internal" } }).then(({ data }) => setManagers((data || []).filter((u) => u.status === "active"))).catch(() => {});
    api.get("/assets").then(({ data }) => setAssets((data || []).filter((a) => !a.assigned_to))).catch(() => {});
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (arr, v) => arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  const valid = () => {
    if (step === 0 && (!form.email.trim() || !form.name.trim())) return "Email + name required";
    return null;
  };
  const next = () => { const e = valid(); if (e) { toast.error(e); return; } setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/onboarding/employee", form);
      toast.success(`${data.party.name} invited. ${data.assets_assigned.length} asset(s), ${data.checklist.items.length} checklist item(s).`);
      onOpenChange(false); onCreated && onCreated(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const bodies = {
    identity: (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="eob-identity">
        <div><Label className="label-overline">Email *</Label><Input className="rounded-sm mt-1" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="eob-email" /></div>
        <div><Label className="label-overline">Name *</Label><Input className="rounded-sm mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="eob-name" /></div>
        <div>
          <Label className="label-overline">Role</Label>
          <Select value={form.role} onValueChange={(v) => { set("role", v); if (["designer", "engineer", "intern"].includes(v)) set("plan_template", v); }}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="eob-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["designer", "engineer", "strategist", "operations", "client_manager", "intern", "admin"].map((r) => (
                <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-overline">BU</Label>
          <Select value={form.business_unit} onValueChange={(v) => set("business_unit", v)}>
            <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="Strategy">Strategy</SelectItem><SelectItem value="BU1">BU1</SelectItem><SelectItem value="BU2">BU2</SelectItem><SelectItem value="BU3">BU3</SelectItem><SelectItem value="Operations">Operations</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label className="label-overline">Title</Label><Input className="rounded-sm mt-1" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Sr. Designer" /></div>
        <div>
          <Label className="label-overline">Manager</Label>
          <Select value={form.manager_id || "__none"} onValueChange={(v) => set("manager_id", v === "__none" ? "" : v)}>
            <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent><SelectItem value="__none">No manager</SelectItem>{managers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="label-overline">Start date</Label><Input type="date" className="rounded-sm mt-1" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
        <div>
          <Label className="label-overline">Type</Label>
          <Select value={form.employee_type} onValueChange={(v) => set("employee_type", v)}>
            <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="full_time">Full time</SelectItem><SelectItem value="part_time">Part time</SelectItem><SelectItem value="contractor">Contractor</SelectItem><SelectItem value="intern">Intern</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
    ),
    access: (
      <div className="space-y-3" data-testid="eob-access">
        <p className="text-xs text-slate-500">They&apos;ll be added to the project channels + #general. Magic-link invite + admin approval before access.</p>
        <div className="space-y-1 max-h-[300px] overflow-y-auto pr-1">
          {projects.map((p) => {
            const picked = form.project_ids.includes(p.id);
            return (
              <label key={p.id} className={`flex items-center gap-2 px-3 py-2 rounded-sm border-2 ${picked ? "border-[#F54800] bg-[#F54800]/5" : "border-slate-200"}`}>
                <input type="checkbox" checked={picked} onChange={() => set("project_ids", toggle(form.project_ids, p.id))} data-testid={`eob-project-${p.id}`} />
                <span className="text-xs">{p.name}</span>
                <span className="text-[10px] text-slate-400 ml-auto">{p.business_unit}</span>
              </label>
            );
          })}
        </div>
      </div>
    ),
    equipment: (
      <div className="space-y-3" data-testid="eob-equipment">
        <div>
          <div className="label-overline mb-1">Assign from register</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-[180px] overflow-y-auto">
            {assets.length === 0 ? <div className="text-[11px] text-slate-400">No unassigned assets in the register.</div> :
              assets.map((a) => {
                const picked = form.asset_ids.includes(a.id);
                return (
                  <label key={a.id} className={`flex items-center gap-2 px-2 py-1 rounded-sm border ${picked ? "border-[#F54800] bg-[#F54800]/5" : "border-slate-200"}`}>
                    <input type="checkbox" checked={picked} onChange={() => set("asset_ids", toggle(form.asset_ids, a.id))} data-testid={`eob-asset-${a.id}`} />
                    <span className="text-[11px]">{a.name} · {a.category}</span>
                  </label>
                );
              })}
          </div>
        </div>
        <div>
          <div className="label-overline mb-1">OR raise a purchase request for missing equipment</div>
          <div className="flex items-center gap-2">
            <Input className="rounded-sm flex-1" placeholder="e.g. MacBook Pro 14, second monitor" value={procureDraft} onChange={(e) => setProcureDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && procureDraft.trim()) { set("procure_assets", [...form.procure_assets, procureDraft.trim()]); setProcureDraft(""); } }} data-testid="eob-procure-input" />
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { if (procureDraft.trim()) { set("procure_assets", [...form.procure_assets, procureDraft.trim()]); setProcureDraft(""); } }}><Plus size={11} /></Button>
          </div>
          {form.procure_assets.length > 0 && (
            <ul className="mt-2 space-y-1">
              {form.procure_assets.map((p, i) => (
                <li key={`${p}-${i}`} className="text-[11px] flex items-center justify-between bg-slate-50 px-2 py-1 rounded-sm">
                  <span>{p}</span>
                  <button onClick={() => set("procure_assets", form.procure_assets.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X size={10} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    ),
    docs: (
      <div className="space-y-3" data-testid="eob-docs">
        <p className="text-xs text-slate-500">Picked items create document requests in their My Documents. Pending until they upload.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
          {DEFAULT_DOCS.map((d) => {
            const picked = form.request_documents.includes(d);
            return (
              <button key={d} onClick={() => set("request_documents", toggle(form.request_documents, d))} className={`text-xs px-2 py-1.5 rounded-sm border-2 ${picked ? "border-[#F54800] bg-[#F54800]/5" : "border-slate-200"}`} data-testid={`eob-doc-${d.replace(/\s+/g, "-")}`}>{d}</button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <Input className="rounded-sm flex-1" placeholder="Add custom request…" value={docDraft} onChange={(e) => setDocDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && docDraft.trim()) { set("request_documents", [...form.request_documents, docDraft.trim()]); setDocDraft(""); } }} />
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { if (docDraft.trim()) { set("request_documents", [...form.request_documents, docDraft.trim()]); setDocDraft(""); } }}><Plus size={11} /></Button>
        </div>
      </div>
    ),
    plan: (
      <div className="space-y-3" data-testid="eob-plan">
        <div>
          <Label className="label-overline">Template</Label>
          <Select value={form.plan_template} onValueChange={(v) => set("plan_template", v)}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="eob-template"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default</SelectItem>
              <SelectItem value="designer">Designer</SelectItem>
              <SelectItem value="engineer">Engineer</SelectItem>
              <SelectItem value="intern">Intern</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-400 mt-1">Each template seeds 5–6 first-week tasks. Add custom ones below.</p>
        </div>
        <div>
          <div className="label-overline mb-1">Extra tasks</div>
          <div className="flex items-center gap-2">
            <Input className="rounded-sm flex-1" placeholder="e.g. Lunch with founders" value={planDraft} onChange={(e) => setPlanDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && planDraft.trim()) { set("custom_checklist", [...form.custom_checklist, planDraft.trim()]); setPlanDraft(""); } }} data-testid="eob-task-input" />
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { if (planDraft.trim()) { set("custom_checklist", [...form.custom_checklist, planDraft.trim()]); setPlanDraft(""); } }}><Plus size={11} /></Button>
          </div>
          {form.custom_checklist.length > 0 && (
            <ul className="mt-2 space-y-1">
              {form.custom_checklist.map((c, i) => (
                <li key={`${c}-${i}`} className="text-[11px] flex items-center justify-between bg-slate-50 px-2 py-1 rounded-sm">
                  <span>{c}</span>
                  <button onClick={() => set("custom_checklist", form.custom_checklist.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><X size={10} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    ),
    review: (
      <div className="card-flat p-3 bg-[#F54800]/5 border-l-4 border-l-[#F54800]" data-testid="eob-review">
        <div className="text-xs font-display font-bold text-slate-900 mb-1">About to create:</div>
        <ul className="text-[11px] text-slate-700 space-y-0.5">
          <li>• {form.name} ({form.email}) · {form.role.replace(/_/g, " ")} · {form.business_unit}</li>
          <li>• Magic-link invite emailed; status starts as <b>invited</b>, becomes active after they finish onboarding form &amp; you approve</li>
          <li>• Added to {form.project_ids.length} project(s) and #general</li>
          <li>• {form.asset_ids.length} asset(s) assigned, {form.procure_assets.length} purchase request(s) raised</li>
          <li>• {form.request_documents.length} document request(s) sent</li>
          <li>• {form.plan_template} checklist + {form.custom_checklist.length} custom item(s)</li>
        </ul>
      </div>
    ),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] flex flex-col p-0" data-testid="employee-onboarding-wizard">
        <DialogHeader className="p-5 border-b border-slate-100 shrink-0"><DialogTitle className="font-display text-lg">Onboard employee · {STEPS[step].label}</DialogTitle><DialogDescription className="sr-only">Multi-step wizard to onboard a new employee: identity, access, equipment, documents, first-week plan, and review.</DialogDescription></DialogHeader>
        <div className="flex items-center gap-1 px-4 py-2 border-b border-slate-100 overflow-x-auto shrink-0">
          {STEPS.map((s, i) => {
            const Icon = s.icon; const active = i === step; const done = i < step;
            return (
              <button key={s.id} onClick={() => i <= step && setStep(i)} className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-sm text-[11px] font-semibold ${active ? "bg-slate-900 text-white" : done ? "text-[#F54800]" : "text-slate-400"}`} data-testid={`eob-step-${s.id}`}>
                {done ? <Check size={11} /> : <Icon size={11} />}<span>{s.label}</span>
              </button>
            );
          })}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          <p className="text-[11px] text-slate-500 mb-4">{STEPS[step].hint}</p>
          {bodies[STEPS[step].id]}
        </div>
        <div className="flex items-center justify-between p-4 border-t border-slate-100 shrink-0">
          <Button variant="ghost" onClick={back} disabled={step === 0} className="rounded-sm"><ChevronLeft size={13} className="mr-1" /> Back</Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="eob-next">Next <ChevronRight size={13} className="ml-1" /></Button>
          ) : (
            <Button onClick={submit} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="eob-submit">
              {saving ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Check size={13} className="mr-1.5" />}Create &amp; send invite
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
